import type { z } from "zod";

/** Shared GET helper for the app's own JSON API.
 *
 *  - Sends the request, throws on non-2xx with a server-supplied message when
 *    available.
 *  - Unwraps the standard `{ data: ... }` envelope returned by `Response.json`
 *    handlers in `src/index.ts` (falls back to the raw payload if the envelope
 *    isn't there).
 *  - **Validates the response against a Zod schema**, replacing the previous
 *    pattern of casting `as T` at the boundary (banned by code-quality rules).
 *  - Forwards the dev-only `?as=<sam>` impersonation override from
 *    `window.location` so identity-scoped endpoints respect the override.
 *
 *  Example:
 *    const FormsSchema = z.array(z.object({ id: z.string(), ... }));
 *    const forms = await apiGet("/api/policies/WC-001/forms", FormsSchema);
 */
export async function apiGet<T>(url: string, schema: z.ZodType<T>): Promise<T> {
  const res = await fetch(withAsOverride(url));
  if (!res.ok) {
    let msg = `Request failed: ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      msg = body.error ?? body.message ?? msg;
    } catch {
      // Body wasn't JSON — fall back to the status-code message.
    }
    throw new Error(msg);
  }
  const json = (await res.json()) as { data?: unknown } | unknown;
  const payload =
    json !== null && typeof json === "object" && "data" in json
      ? (json as { data: unknown }).data
      : json;
  return schema.parse(payload);
}

/** Shared helper for non-GET JSON requests (POST / PUT / PATCH / DELETE).
 *
 *  - Sends `body` as JSON when provided (the `Content-Type` header is set
 *    automatically); omits the body for DELETEs that take no payload.
 *  - Surfaces the server's `{ error: ... }` / `{ message: ... }` text on
 *    non-2xx responses so callers can show a real message in toasts.
 *  - Validates the response against `schema` — the response is unwrapped
 *    from the standard `{ data: ... }` envelope first.
 *  - Forwards the dev-only `?as=` override.
 */
export async function apiSend<T>(
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  url: string,
  body: unknown,
  schema: z.ZodType<T>,
): Promise<T> {
  const init: RequestInit = { method };
  if (body !== undefined) {
    init.headers = { "Content-Type": "application/json" };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(withAsOverride(url), init);
  if (!res.ok) {
    let msg = `Request failed: ${res.status}`;
    try {
      const errBody = (await res.json()) as { error?: string; message?: string };
      msg = errBody.error ?? errBody.message ?? msg;
    } catch {
      // Body wasn't JSON — fall back to the status-code message.
    }
    throw new Error(msg);
  }
  // Some mutations return 204 No Content — schema must allow `undefined` /
  // `null` in that case, callers know what to expect.
  if (res.status === 204) return schema.parse(undefined);
  const json = (await res.json()) as { data?: unknown } | unknown;
  const payload =
    json !== null && typeof json === "object" && "data" in json
      ? (json as { data: unknown }).data
      : json;
  return schema.parse(payload);
}

/** Append the dev-only `?as=<sam>` override (when present in the page URL) to
 *  an outgoing API URL so impersonation testing stays consistent across hooks
 *  and call sites. The server-side helper in `src/lib/restriction.ts` only
 *  honors this in non-production environments. */
export function withAsOverride(url: string): string {
  if (typeof window === "undefined") return url;
  const as = new URLSearchParams(window.location.search).get("as");
  if (!as) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}as=${encodeURIComponent(as)}`;
}

/** Read the `?as=` override as a stable string for inclusion in TanStack Query
 *  cache keys — different identities must not share a cache entry. */
export function asOverrideKey(): string {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("as") ?? "";
}
